import { ReactNode } from 'react';

interface Props {
    inventoryTotal: number;
    selectedCount: number;
    selectedAmount: number;
    range?: [number, number]; // present in JOIN mode
    chance?: number; // present in JOIN mode (0..100)
    minBet?: number; // present in CREATE mode
    onAction: () => void;
    actionLabel: string;
    actionDisabled: boolean;
}

const Icon = ({ d }: { d: string }) => (
    <svg
        viewBox="0 0 24 24"
        className="w-3.5 h-3.5 shrink-0 stroke-current fill-none [stroke-width:2] [stroke-linecap:round] [stroke-linejoin:round]"
    >
        {d.split('|').map((p, i) => (
            <path key={i} d={p} />
        ))}
    </svg>
);

const ICONS = {
    bag: 'M6 7h12l-1 13H7L6 7z|M9 7a3 3 0 0 1 6 0',
    coin: 'M12 3v18|M16.5 7.5C16.5 6 14.4 5 12 5S7.5 6 7.5 7.5 9.6 10 12 10s4.5 1 4.5 2.5S14.4 15 12 15s-4.5-1-4.5-2.5',
    range: 'M4 12h16|M8 8l-4 4 4 4|M16 8l4 4-4 4',
    target: 'M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18|M12 8a4 4 0 1 0 0 8 4 4 0 0 0 0-8',
    check: 'M20 6 9 17l-5-5',
};

const Pill = ({
    icon,
    label,
    children,
    highlight,
}: {
    icon: keyof typeof ICONS;
    label: string;
    children: ReactNode;
    highlight?: boolean;
}) => (
    <span
        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-fl text-xs text-text_3 font-medium whitespace-nowrap"
        style={
            highlight
                ? {
                      border: '1px solid color-mix(in srgb, #3ad6ff 35%, transparent)',
                      background: 'color-mix(in srgb, #3ad6ff 8%, transparent)',
                  }
                : { border: '1px solid var(--stroke, rgba(150,130,255,0.16))' }
        }
    >
        <span className={highlight ? 'text-[#5fe0ff]' : 'text-text_4'}>
            <Icon d={ICONS[icon]} />
        </span>
        {label}
        <b
            className="font-chat-nick font-extrabold ml-0.5 tabular-nums"
            style={
                highlight
                    ? { color: '#5fe0ff', textShadow: '0 0 10px rgba(58,214,255,0.45)' }
                    : { color: 'var(--text-main, #fff)' }
            }
        >
            {children}
        </b>
    </span>
);

const CoinflipJoinFooter = ({
    inventoryTotal,
    selectedCount,
    selectedAmount,
    range,
    chance,
    minBet,
    onAction,
    actionLabel,
    actionDisabled,
}: Props) => {
    const minMet = minBet === undefined || selectedAmount >= minBet;
    return (
        <div className="border-t border-stroke pt-3.5 mt-3 flex justify-between items-center gap-3.5 flex-wrap max-md:flex-col max-md:items-stretch max-md:gap-2.5">
            <div className="flex gap-2.5 items-center flex-wrap max-md:justify-center">
                <Pill icon="bag" label="Inventory">
                    ${inventoryTotal.toFixed(2)}
                </Pill>
                {minBet !== undefined && (
                    <span
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-fl text-xs text-text_3 font-medium whitespace-nowrap"
                        style={{
                            border: `1px solid ${minMet ? 'rgba(150,130,255,0.16)' : 'rgba(255,98,98,0.4)'}`,
                            background: minMet ? 'transparent' : 'rgba(255,98,98,0.08)',
                        }}
                    >
                        <span className={minMet ? 'text-text_4' : 'text-[#ff8a8a]'}>
                            <Icon d={ICONS.coin} />
                        </span>
                        Min bet
                        <b
                            className="font-chat-nick font-extrabold ml-0.5 tabular-nums"
                            style={{ color: minMet ? '#fff' : '#ff8a8a' }}
                        >
                            ${minBet.toFixed(2)}
                        </b>
                    </span>
                )}
                {range && (
                    <Pill icon="range" label="Range">
                        ${range[0].toFixed(2)} – ${range[1].toFixed(2)}
                    </Pill>
                )}
                {chance !== undefined && (
                    <Pill icon="target" label="Chance">
                        {chance.toFixed(2)}%
                    </Pill>
                )}
            </div>

            <div className="flex gap-2.5 items-center flex-wrap max-md:w-full max-md:justify-between">
                <Pill icon="check" label="Selected">
                    {selectedCount}
                </Pill>
                <Pill icon="coin" label="Amount" highlight>
                    ${selectedAmount.toFixed(2)}
                </Pill>
                <button
                    onClick={onAction}
                    disabled={actionDisabled}
                    className="group relative overflow-hidden inline-flex justify-center items-center gap-2 px-7 py-2.5 rounded-full text-[13px] font-extrabold tracking-[0.1em] uppercase max-md:w-full max-md:order-last text-[#04150c] disabled:opacity-40 disabled:cursor-not-allowed enabled:hover:-translate-y-0.5 transition-transform"
                    style={{
                        background: 'linear-gradient(180deg, #6df0a9, #23b06a)',
                        boxShadow:
                            'inset 0 1px 0 rgba(255,255,255,0.45), inset 0 0 0 1px rgba(255,255,255,0.18), 0 0 26px -4px rgba(70,224,138,0.75), 0 8px 22px -6px rgba(70,224,138,0.6)',
                    }}
                >
                    <span
                        aria-hidden
                        className="pointer-events-none absolute inset-0 -translate-x-[120%] group-hover:translate-x-[120%] transition-transform duration-[700ms] ease-out"
                        style={{
                            background:
                                'linear-gradient(100deg, transparent 30%, rgba(255,255,255,0.55), transparent 70%)',
                        }}
                    />
                    <svg
                        viewBox="0 0 24 24"
                        className="relative w-3.5 h-3.5 stroke-current fill-none [stroke-width:2.6] [stroke-linecap:round] [stroke-linejoin:round]"
                    >
                        <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4" />
                        <path d="M10 17l5-5-5-5" />
                        <path d="M15 12H3" />
                    </svg>
                    <span className="font-chat-nick relative">{actionLabel}</span>
                </button>
            </div>
        </div>
    );
};

export default CoinflipJoinFooter;
